import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Paper,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconEdit, IconPlus } from '@tabler/icons-react';

import Taggit, { TagPill } from './Taggit';

const CUES_VARIETY = 'coaching_cues';

export default function ExerciseTagsPanel({
  exerciseId,
  tagsByVariety = {},
  varieties = [],
  readOnly = false,
  onChanged = () => {},
}) {
  const [editingVariety, setEditingVariety] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addValue, setAddValue] = useState(null);

  const libraryVarieties = useMemo(
    () => varieties.filter((variety) => variety.value !== CUES_VARIETY),
    [varieties]
  );

  const filledRows = useMemo(
    () =>
      libraryVarieties.filter((variety) => {
        const tags = tagsByVariety[variety.value];
        return Array.isArray(tags) && tags.length > 0;
      }),
    [libraryVarieties, tagsByVariety]
  );

  const visibleRows = useMemo(() => {
    if (!editingVariety) return filledRows;
    const alreadyShown = filledRows.some(
      (variety) => variety.value === editingVariety
    );
    if (alreadyShown) return filledRows;
    const pending = libraryVarieties.find(
      (variety) => variety.value === editingVariety
    );
    return pending ? [...filledRows, pending] : filledRows;
  }, [filledRows, editingVariety, libraryVarieties]);

  const emptyVarieties = useMemo(
    () =>
      libraryVarieties.filter((variety) => {
        const tags = tagsByVariety[variety.value];
        return !Array.isArray(tags) || tags.length === 0;
      }),
    [libraryVarieties, tagsByVariety]
  );

  function openEditor(varietyValue) {
    setAdding(false);
    setAddValue(null);
    setEditingVariety(varietyValue);
  }

  function closeEditor() {
    setEditingVariety(null);
  }

  function onPickEmptyVariety(value) {
    if (!value) return;
    setAddValue(value);
    openEditor(value);
  }

  return (
    <Paper p="md" withBorder radius="sm">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={700}>Library tags</Text>
            <Text size="sm" c="dimmed">
              Movement, equipment, and other catalog metadata.
            </Text>
          </div>
          {!readOnly && emptyVarieties.length > 0 && !adding && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setAdding(true)}
            >
              Add tags
            </Button>
          )}
        </Group>

        {!readOnly && adding && (
          <Select
            label="Variety"
            placeholder="Choose a variety…"
            data={emptyVarieties.map((variety) => ({
              value: variety.value,
              label: variety.text,
            }))}
            value={addValue}
            onChange={onPickEmptyVariety}
            searchable
            clearable
            onClear={() => {
              setAdding(false);
              setAddValue(null);
            }}
          />
        )}

        {visibleRows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No library tags yet.
          </Text>
        ) : (
          <Stack gap="sm">
            {visibleRows.map((variety) => {
              const tags = tagsByVariety[variety.value] || [];
              const isEditing =
                !readOnly && editingVariety === variety.value;

              return (
                <Stack key={variety.value} gap={6}>
                  <Text size="sm" fw={600}>
                    {variety.text}
                  </Text>
                  {isEditing ? (
                    <Taggit
                      key={`edit-${variety.value}`}
                      reqInfo={{
                        exercise_id: exerciseId,
                        variety: variety.value,
                      }}
                      fetchData={onChanged}
                      defaultEditing
                      onDone={closeEditor}
                    />
                  ) : (
                    <Flex gap="xs" wrap="wrap" align="center">
                      {tags.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          start tagging
                        </Text>
                      ) : (
                        tags.map((tagName) => (
                          <TagPill
                            key={`${variety.value}-${tagName}`}
                            tag={{ name: tagName }}
                          />
                        ))
                      )}
                      {!readOnly && (
                        <ActionIcon
                          size="xs"
                          color="gray"
                          radius="xl"
                          variant="transparent"
                          aria-label={`Edit ${variety.text}`}
                          onClick={() => openEditor(variety.value)}
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                      )}
                    </Flex>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
